package com.heelion.farmafreelance.api

import io.ktor.client.*
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.client.plugins.logging.*
import io.ktor.client.request.*
import io.ktor.http.*
import io.ktor.client.call.*
import io.ktor.client.statement.bodyAsText
import io.ktor.serialization.kotlinx.json.*
import kotlinx.serialization.json.Json
import com.heelion.farmafreelance.*
import com.heelion.farmafreelance.models.*

class FarmaFreelanceApi {
    companion object {
        /**
         * Token di sessione ricevuto al login: va in ogni richiesta, così il server sa chi è
         * l'utente senza fidarsi dell'email nei parametri. Null quando nessuno è collegato.
         */
        var token: String? = null

        /** Parametro da aggiungere ai link che si aprono nel browser (PDF, CSV), dove non ci sono header. */
        fun parametroToken(): String = token?.let { "&t=$it" } ?: ""

        /** Chiamato quando il server rifiuta la sessione (scaduta o chiusa da un cambio password). */
        var allaSessioneScaduta: (() -> Unit)? = null
    }

    private val client = HttpClient {
        install(io.ktor.client.plugins.api.createClientPlugin("TokenSessione") {
            onRequest { request, _ ->
                if (!request.headers.contains(HttpHeaders.Authorization)) {
                    token?.let { request.headers.append(HttpHeaders.Authorization, "Bearer $it") }
                }
            }
            onResponse { response ->
                // Solo per le richieste fatte con la sessione corrente: il login sbagliato è un altro 401.
                val conSessione = response.call.request.headers[HttpHeaders.Authorization] == "Bearer $token"
                if (response.status == HttpStatusCode.Unauthorized && token != null && conSessione) {
                    allaSessioneScaduta?.invoke()
                }
            }
        })
        install(ContentNegotiation) {
            json(Json {
                ignoreUnknownKeys = true
                useAlternativeNames = false
            })
        }
        install(Logging) {
            level = LogLevel.ALL
            sanitizeHeader { it == HttpHeaders.Authorization } // il token non finisce nei log
        }
        install(io.ktor.client.plugins.HttpTimeout) {
            requestTimeoutMillis = 60_000 // Aspetta fino a 60 secondi
            connectTimeoutMillis = 60_000
            socketTimeoutMillis = 60_000
        }
    }

    suspend fun login(request: LoginRequest): LoginResponse {
        return try {
            val response = client.post("$BASE_URL/login") {
                contentType(ContentType.Application.Json)
                setBody(request)
            }
            response.body<LoginResponse>()
        } catch (e: Exception) {
            LoginResponse(success = false, message = e.message ?: "Errore di connessione")
        }
    }

    // --- Verifica in due passaggi (app di autenticazione) ---

    /** Secondo passaggio del login: codice a 6 cifre (o di emergenza) con il token temporaneo. */
    suspend fun loginCodice(tokenTemporaneo: String, codice: String): LoginResponse = try {
        client.post("$BASE_URL/login/codice") {
            contentType(ContentType.Application.Json)
            setBody(CodiceDueFattoriRequest(tokenTemporaneo, codice))
        }.body()
    } catch (e: Exception) {
        LoginResponse(success = false, message = "Errore di connessione, riprova.")
    }

    suspend fun statoDueFattori(): DueFattoriStato? = try {
        client.get("$BASE_URL/api/2fa/stato").takeIf { it.status == HttpStatusCode.OK }?.body()
    } catch (e: Exception) {
        null
    }

    suspend fun iniziaDueFattori(): DueFattoriAvvio? = try {
        client.post("$BASE_URL/api/2fa/inizia").takeIf { it.status == HttpStatusCode.OK }?.body()
    } catch (e: Exception) {
        null
    }

    /** Codici di emergenza se il codice è giusto, altrimenti null. */
    suspend fun confermaDueFattori(codice: String): DueFattoriCodiciEmergenza? = try {
        client.post("$BASE_URL/api/2fa/conferma") {
            contentType(ContentType.Application.Json)
            setBody(DueFattoriCodice(codice))
        }.takeIf { it.status == HttpStatusCode.OK }?.body()
    } catch (e: Exception) {
        null
    }

    suspend fun disattivaDueFattori(password: String, codice: String): Boolean = try {
        client.post("$BASE_URL/api/2fa/disattiva") {
            contentType(ContentType.Application.Json)
            setBody(DueFattoriDisattiva(password, codice))
        }.status == HttpStatusCode.OK
    } catch (e: Exception) {
        false
    }

    /** Consensi facoltativi (email promozionali); null se non si riesce a leggerli. */
    suspend fun consensi(): ConsensiAccount? = try {
        client.get("$BASE_URL/api/account/consensi").takeIf { it.status == HttpStatusCode.OK }?.body()
    } catch (e: Exception) {
        null
    }

    suspend fun salvaConsensi(consensi: ConsensiAccount): Boolean = try {
        client.post("$BASE_URL/api/account/consensi") {
            contentType(ContentType.Application.Json)
            setBody(consensi)
        }.status == HttpStatusCode.OK
    } catch (e: Exception) {
        false
    }

    /** "Elimina account": l'account si chiude subito e dopo 30 giorni i dati vengono cancellati. */
    suspend fun eliminaAccount(password: String): PasswordResetResponse = try {
        client.post("$BASE_URL/api/account/elimina") {
            contentType(ContentType.Application.Json)
            setBody(EliminaAccountRequest(password))
        }.body()
    } catch (e: Exception) {
        PasswordResetResponse(false, "Errore di connessione: riprova.")
    }

    /** Accesso automatico con il token salvato: risponde come il login, con un token rinnovato. */
    suspend fun riprendiSessione(): LoginResponse = try {
        client.get("$BASE_URL/sessione").body()
    } catch (e: Exception) {
        LoginResponse(success = false, message = e.message ?: "Errore di connessione")
    }

    /** "Non ricordi l'email?": email mascherate legate a un codice fiscale o una partita IVA. */
    suspend fun recuperaEmail(codice: String): RecuperoEmailResponse = try {
        client.post("$BASE_URL/auth/recupera-email") {
            contentType(ContentType.Application.Json)
            setBody(RecuperoEmailRequest(codice))
        }.body()
    } catch (e: Exception) {
        RecuperoEmailResponse(message = "Errore di connessione, riprova.")
    }

    /** "Ho perso l'accesso": richiesta all'amministratore per cambiare l'email dell'account. */
    suspend fun chiediRecuperoAccesso(richiesta: RecuperoAccessoRequest): PasswordResetResponse = try {
        client.post("$BASE_URL/auth/recupero-accesso") {
            contentType(ContentType.Application.Json)
            setBody(richiesta)
        }.body()
    } catch (e: Exception) {
        PasswordResetResponse(success = false, message = "Errore di connessione, riprova.")
    }

    suspend fun requestPasswordReset(email: String): PasswordResetResponse {
        println("DEBUG API: requestPasswordReset - base: $BASE_URL, email: $email")
        return try {
            val response = client.post("$BASE_URL/auth/password-reset/request") {
                contentType(ContentType.Application.Json)
                setBody(PasswordResetRequest(email))
            }
            println("DEBUG API: requestPasswordReset status: ${response.status}")
            val body = response.body<PasswordResetResponse>()
            println("DEBUG API: requestPasswordReset body: $body")
            body
        } catch (e: Exception) {
            println("DEBUG API: requestPasswordReset ERROR: ${e.message}")
            PasswordResetResponse(success = false, message = e.message ?: "Errore di connessione")
        }
    }

    suspend fun confirmPasswordReset(email: String, code: String, newPassword: String): PasswordResetResponse {
        println("DEBUG API: confirmPasswordReset - base: $BASE_URL, email: $email, code: $code")
        return try {
            val response = client.post("$BASE_URL/auth/password-reset/confirm") {
                contentType(ContentType.Application.Json)
                setBody(PasswordResetConfirmRequest(email, code, newPassword))
            }
            println("DEBUG API: confirmPasswordReset status: ${response.status}")
            val body = response.body<PasswordResetResponse>()
            println("DEBUG API: confirmPasswordReset body: $body")
            body
        } catch (e: Exception) {
            println("DEBUG API: confirmPasswordReset ERROR: ${e.message}")
            PasswordResetResponse(success = false, message = e.message ?: "Errore di connessione")
        }
    }

    suspend fun registerFarmacista(farmacista: Farmacista): Boolean {
        return try {
            val response = client.post("$BASE_URL/register/farmacista") {
                contentType(ContentType.Application.Json)
                setBody(farmacista)
            }
            response.status == HttpStatusCode.Created
        } catch (e: Exception) {
            false
        }
    }

    /** Iscrizione di un infermiere: "OK", "EMAIL_USATA" (email già di un altro account) o "ERRORE". */
    suspend fun registerInfermiere(infermiere: Infermiere): String {
        return try {
            val response = client.post("$BASE_URL/register/infermiere") {
                contentType(ContentType.Application.Json)
                setBody(infermiere)
            }
            when (response.status) {
                HttpStatusCode.Created -> "OK"
                HttpStatusCode.Conflict -> "EMAIL_USATA"
                else -> "ERRORE"
            }
        } catch (e: Exception) {
            "ERRORE"
        }
    }

    suspend fun updateInfermiereProfilo(email: String, infermiere: Infermiere): Boolean {
        return try {
            client.patch("$BASE_URL/api/infermiere/profilo") {
                parameter("email", email)
                contentType(ContentType.Application.Json)
                setBody(infermiere)
            }.status == HttpStatusCode.OK
        } catch (e: Exception) {
            false
        }
    }

    suspend fun getInfermiereByEmail(email: String): Infermiere? {
        return try {
            val response = client.get("$BASE_URL/api/infermiere/profilo") { parameter("email", email) }
            if (response.status == HttpStatusCode.OK) response.body() else null
        } catch (e: Exception) {
            null
        }
    }

    suspend fun getInfermiereById(id: String): Infermiere? {
        return try {
            val response = client.get("$BASE_URL/api/infermiere/$id")
            if (response.status == HttpStatusCode.OK) response.body() else null
        } catch (e: Exception) {
            null
        }
    }

    /** Come [searchFarmacisti], per le farmacie che cercano un infermiere. */
    suspend fun searchInfermieri(
        query: String = "",
        comune: String = "",
        provincia: String = "",
        startDate: Long? = null,
        endDate: Long? = null,
        myEmail: String? = null,
        competenza: String = "",
        regione: String = "",
        cap: String = ""
    ): List<Infermiere> {
        return try {
            client.get("$BASE_URL/infermieri/search") {
                if (regione.isNotEmpty()) parameter("regione", regione)
                if (cap.isNotEmpty()) parameter("cap", cap)
                if (query.isNotEmpty()) parameter("query", query)
                if (comune.isNotEmpty()) parameter("comune", comune)
                if (provincia.isNotEmpty()) parameter("provincia", provincia)
                if (startDate != null) parameter("startDate", startDate)
                if (endDate != null) parameter("endDate", endDate)
                if (myEmail != null) parameter("myEmail", myEmail)
                if (competenza.isNotEmpty()) parameter("competenza", competenza)
            }.body()
        } catch (e: Exception) {
            emptyList()
        }
    }

    suspend fun registerFarmacia(farmacia: Farmacia): Boolean {
        return try {
            val response = client.post("$BASE_URL/register/farmacia") {
                contentType(ContentType.Application.Json)
                setBody(farmacia)
            }
            response.status == HttpStatusCode.Created
        } catch (e: Exception) {
            false
        }
    }

    suspend fun updateFarmacistaProfilo(email: String, farmacista: Farmacista): Boolean {
        return try {
            val response = client.patch("$BASE_URL/api/farmacista/profilo") {
                parameter("email", email)
                contentType(ContentType.Application.Json)
                setBody(farmacista)
            }
            response.status == HttpStatusCode.OK
        } catch (e: Exception) {
            false
        }
    }

    suspend fun updateFarmaciaProfilo(email: String, farmacia: Farmacia): Boolean {
        return try {
            val response = client.patch("$BASE_URL/api/farmacia/profilo") {
                parameter("email", email)
                contentType(ContentType.Application.Json)
                setBody(farmacia)
            }
            response.status == HttpStatusCode.OK
        } catch (e: Exception) {
            false
        }
    }

    suspend fun createAnnuncio(annuncio: AnnuncioTurno): String {
        return try {
            val response = client.post("$BASE_URL/api/annunci") {
                contentType(ContentType.Application.Json)
                setBody(annuncio)
            }
            if (response.status == HttpStatusCode.Created) "SUCCESS"
            else if (response.status == HttpStatusCode.Conflict) "CONFLICT"
            else "ERROR"
        } catch (e: Exception) {
            "ERROR"
        }
    }

    suspend fun createBatchAnnunci(request: BatchAnnuncioRequest): String {
        println("DEBUG API: createBatchAnnunci - invio ${request.entries.size} turni")
        return try {
            val response = client.post("$BASE_URL/api/annunci") {
                contentType(ContentType.Application.Json)
                setBody(request)
            }
            println("DEBUG API: createBatchAnnunci status: ${response.status}")
            if (response.status == HttpStatusCode.Created) "SUCCESS"
            else if (response.status == HttpStatusCode.Conflict) "CONFLICT"
            else "ERROR"
        } catch (e: Exception) {
            println("DEBUG API: createBatchAnnunci EXCEPTION: ${e.message}")
            "ERROR"
        }
    }

    suspend fun searchFarmacie(
        query: String = "",
        regione: String = "",
        provincia: String = "",
        comune: String = "",
        cap: String = "",
        startDate: Long? = null,
        endDate: Long? = null,
        myEmail: String? = null
    ): List<Farmacia> {
        println("DEBUG API: searchFarmacie - base: $BASE_URL, query: $query, regione: $regione")
        return try {
            val response = client.get("$BASE_URL/farmacie/search") {
                if (query.isNotEmpty()) parameter("query", query)
                if (regione.isNotEmpty()) parameter("regione", regione)
                if (provincia.isNotEmpty()) parameter("provincia", provincia)
                if (comune.isNotEmpty()) parameter("comune", comune)
                if (cap.isNotEmpty()) parameter("cap", cap)
                if (startDate != null) parameter("startDate", startDate)
                if (endDate != null) parameter("endDate", endDate)
                if (myEmail != null) parameter("myEmail", myEmail)
            }
            println("DEBUG API: searchFarmacie status: ${response.status}")
            response.body()
        } catch (e: Exception) {
            println("DEBUG API: searchFarmacie EXCEPTION: ${e.message}")
            emptyList()
        }
    }

    suspend fun searchFarmacisti(
        query: String = "",
        comune: String = "",
        cap: String = "",
        startDate: Long? = null,
        endDate: Long? = null,
        myEmail: String? = null,
        competenza: String = "",
        /** Sigle/nomi di provincia separati da virgola: il server li supporta, prima non si passavano. */
        provincia: String = "",
        regione: String = ""
    ): List<Farmacista> {
        return try {
            val response = client.get("$BASE_URL/farmacisti/search") {
                if (regione.isNotEmpty()) parameter("regione", regione)
                if (query.isNotEmpty()) parameter("query", query)
                if (comune.isNotEmpty()) parameter("comune", comune)
                if (cap.isNotEmpty()) parameter("cap", cap)
                if (startDate != null) parameter("startDate", startDate)
                if (endDate != null) parameter("endDate", endDate)
                if (myEmail != null) parameter("myEmail", myEmail)
                if (competenza.isNotEmpty()) parameter("competenza", competenza)
                if (provincia.isNotEmpty()) parameter("provincia", provincia)
            }
            response.body()
        } catch (e: Exception) {
            emptyList()
        }
    }

    suspend fun getAnnunci(
        role: String? = null,
        startDate: Long? = null,
        endDate: Long? = null,
        provincia: String? = null,
        comune: String? = null,
        email: String? = null
    ): List<AnnuncioTurno> {
        return try {
            val response = client.get("$BASE_URL/api/annunci") {
                if (role != null) parameter("role", role)
                if (startDate != null) parameter("startDate", startDate)
                if (endDate != null) parameter("endDate", endDate)
                if (provincia != null) parameter("provincia", provincia)
                if (comune != null) parameter("comune", comune)
                if (email != null) parameter("email", email)
            }
            response.body()
        } catch (e: Exception) {
            emptyList()
        }
    }
    
    /**
     * Turni APERTI pubblicati dalle controparti di [role] (farmacie per un farmacista e
     * viceversa), opzionalmente solo nelle [provincia] indicate (separate da virgola).
     * Senza email di proposito: con l'email il server restituisce i PROPRI annunci.
     * A differenza di [getAnnunci] torna null in caso di errore, così chi chiama può dire
     * "non riesco a caricarli" invece di "non ce ne sono".
     */
    suspend fun getAnnunciControparte(role: String, provincia: String? = null, professione: Professione? = null): List<AnnuncioTurno>? {
        return try {
            client.get("$BASE_URL/api/annunci") {
                parameter("role", role)
                if (provincia != null) parameter("provincia", provincia)
                // Per la farmacia: di chi vuole le disponibilità (senza = farmacisti).
                if (professione != null) parameter("professione", professione.name)
            }.body()
        } catch (e: Exception) {
            println("--- [API] getAnnunciControparte fallita: ${e.message} ---")
            null
        }
    }

    // Rimosso overload ambiguo per email


    suspend fun getAnnuncioById(id: String): AnnuncioTurno? {
        return try {
            val response = client.get("$BASE_URL/api/annunci/$id")
            if (response.status == HttpStatusCode.OK) response.body() else null
        } catch (e: Exception) {
            null
        }
    }

    suspend fun getFarmacistaByEmail(email: String): Farmacista? {
        return try {
            val response = client.get("$BASE_URL/api/farmacista/profilo") {
                parameter("email", email)
            }
            if (response.status == HttpStatusCode.OK) response.body() else null
        } catch (e: Exception) {
            null
        }
    }

    suspend fun getFarmaciaByEmail(email: String): Farmacia? {
        return try {
            val response = client.get("$BASE_URL/api/farmacia/profilo") {
                parameter("email", email)
            }
            if (response.status == HttpStatusCode.OK) response.body() else null
        } catch (e: Exception) {
            null
        }
    }

    suspend fun getFarmacistaById(id: String): Farmacista? {
        return try {
            val response = client.get("$BASE_URL/api/farmacista/$id")
            if (response.status == HttpStatusCode.OK) response.body() else null
        } catch (e: Exception) {
            null
        }
    }

    suspend fun getFarmaciaById(id: String): Farmacia? {
        return try {
            val response = client.get("$BASE_URL/api/farmacia/$id")
            if (response.status == HttpStatusCode.OK) response.body() else null
        } catch (e: Exception) {
            null
        }
    }

    suspend fun updateAnnuncio(id: String, annuncio: AnnuncioTurno): Boolean {
        return try {
            val response = client.patch("$BASE_URL/api/annunci/$id") {
                contentType(ContentType.Application.Json)
                setBody(annuncio)
            }
            response.status == HttpStatusCode.OK
        } catch (e: Exception) {
            false
        }
    }

    suspend fun deleteAnnuncio(id: String): Boolean {
        return try {
            val response = client.delete("$BASE_URL/api/annunci/$id")
            response.status == HttpStatusCode.OK
        } catch (e: Exception) {
            false
        }
    }

    suspend fun deleteBatchAnnunci(ids: List<String>): Boolean {
        return try {
            val response = client.post("$BASE_URL/api/annunci/batch-delete") {
                contentType(ContentType.Application.Json)
                setBody(ids)
            }
            response.status == HttpStatusCode.OK
        } catch (e: Exception) {
            false
        }
    }

    suspend fun getMatches(email: String, role: String): List<Match> {
        return try {
            val response = client.get("$BASE_URL/api/matches") {
                parameter("email", email)
                parameter("role", role)
            }
            response.body()
        } catch (e: Exception) {
            emptyList()
        }
    }

    suspend fun respondToMatch(matchId: String, action: String, email: String, role: String): Pair<Boolean, String?> {
        return try {
            val response = client.post("$BASE_URL/api/matches/$matchId/respond") {
                parameter("action", action)
                parameter("email", email)
                parameter("role", role)
            }
            if (response.status == HttpStatusCode.OK) {
                Pair<Boolean, String?>(true, null)
            } else {
                val errorMsg = try { response.body<String>() } catch (e: Exception) { "" }
                Pair<Boolean, String?>(false, errorMsg.ifBlank { "Errore durante l'operazione" })
            }
        } catch (e: Exception) {
            Pair<Boolean, String?>(false, e.message ?: "Errore di connessione")
        }
    }

    // --- PREFERITI ---
    suspend fun getPreferiti(utenteId: String): List<Preferito> {
        return try {
            client.get("$BASE_URL/api/preferiti") {
                parameter("utenteId", utenteId)
            }.body()
        } catch (e: Exception) {
            emptyList()
        }
    }

    suspend fun togglePreferito(preferito: Preferito): Boolean {
        return try {
            val response = client.post("$BASE_URL/api/preferiti/toggle") {
                contentType(ContentType.Application.Json)
                setBody(preferito)
            }
            response.status == HttpStatusCode.OK || response.status == HttpStatusCode.Created
        } catch (e: Exception) {
            false
        }
    }

    suspend fun sendDirectProposal(targetId: String, targetRuolo: String, message: String, myEmail: String): Boolean {
        return try {
            val response = client.post("$BASE_URL/api/matches/direct-proposal") {
                parameter("targetId", targetId)
                parameter("targetRuolo", targetRuolo)
                parameter("myEmail", myEmail)
                contentType(ContentType.Application.Json)
                setBody(message)
            }
            response.status == HttpStatusCode.OK || response.status == HttpStatusCode.Created
        } catch (e: Exception) {
            false
        }
    }

    /** Il tour di Farmy è stato visto o saltato: il server non lo farà più vedere. */
    suspend fun segnaTutorialVisto(email: String, role: String): Boolean = try {
        client.post("$BASE_URL/api/tutorial-visto") {
            parameter("email", email)
            parameter("role", role)
        }.status == HttpStatusCode.OK
    } catch (e: Exception) {
        false
    }

    /**
     * La farmacia invita un professionista (farmacista o infermiere: lo decide il turno) su un suo
     * turno. null = fatto, altrimenti il messaggio d'errore.
     */
    suspend fun invitaSuTurno(annuncioId: String, professionistaId: String, myEmail: String): String? {
        return try {
            val response = client.post("$BASE_URL/api/matches/invita") {
                parameter("annuncioId", annuncioId)
                parameter("professionistaId", professionistaId)
                parameter("farmacistaId", professionistaId) // nome che capiscono anche i server non aggiornati
                parameter("email", myEmail)
            }
            if (response.status == HttpStatusCode.Created) null else response.bodyAsText().ifBlank { "Errore" }
        } catch (e: Exception) {
            "Server non raggiungibile"
        }
    }

    suspend fun getNotifications(utenteId: String): List<Notification> {
        return try {
            client.get("$BASE_URL/api/notifications") {
                parameter("utenteId", utenteId)
            }.body()
        } catch (e: Exception) {
            emptyList()
        }
    }

    suspend fun getStorico(utenteId: String, role: String): List<StoricoEntry> {
        return try {
            client.get("$BASE_URL/api/matches/storico") {
                parameter("utenteId", utenteId)
                parameter("role", role)
            }.body()
        } catch (e: Exception) {
            emptyList()
        }
    }

    suspend fun creaFatturaProforma(annuncioIds: List<String>, professionistaId: String, farmaciaId: String, role: String = "FARMACISTA"): FatturaEntry? {
        return try {
            val idsParam = annuncioIds.joinToString(",")
            client.post("$BASE_URL/api/matches/fatture/crea") {
                parameter("annuncioIds", idsParam)
                parameter("professionistaId", professionistaId)
                parameter("farmacistaId", professionistaId) // nome che capiscono anche i server non aggiornati
                parameter("farmaciaId", farmaciaId)
                parameter("role", role)
            }.body()
        } catch (e: Exception) {
            null
        }
    }

    /** Come [getStorico], ma null in caso di errore: "non riesco a caricarlo" ≠ "nessun turno svolto". */
    /** Agenda dei pagamenti del professionista: segna (o toglie) "pagato" sui turni [ids] dello storico. */
    suspend fun segnaPagato(professionistaId: String, ids: List<String>, pagato: Boolean, dataMillis: Long?, role: String = "FARMACISTA"): Boolean = try {
        client.post("$BASE_URL/api/matches/storico/pagato") {
            parameter("utenteId", professionistaId)
            parameter("role", role)
            parameter("ids", ids.joinToString(","))
            parameter("pagato", pagato)
            dataMillis?.let { parameter("data", it) }
        }.status == HttpStatusCode.OK
    } catch (e: Exception) {
        false
    }

    suspend fun getStoricoOppureNull(utenteId: String, role: String): List<StoricoEntry>? {
        return try {
            val risposta = client.get("$BASE_URL/api/matches/storico") {
                parameter("utenteId", utenteId)
                parameter("role", role)
            }
            if (risposta.status == HttpStatusCode.OK) risposta.body() else null
        } catch (e: Exception) {
            println("--- [API] getStorico fallita: ${e.message} ---")
            null
        }
    }

    suspend fun getStoricoFatture(utenteId: String, role: String): List<FatturaEntry> {
        return try {
            client.get("$BASE_URL/api/matches/fatture/storico") {
                parameter("utenteId", utenteId)
                parameter("role", role)
            }.body()
        } catch (e: Exception) {
            emptyList()
        }
    }

    /** Registra (o aggiorna) il token push di questo dispositivo per l'utente. */
    suspend fun registraDispositivoPush(dispositivo: DispositivoPush): Boolean {
        return try {
            client.post("$BASE_URL/api/notifications/device") {
                contentType(ContentType.Application.Json)
                setBody(dispositivo)
            }.status == HttpStatusCode.OK
        } catch (e: Exception) {
            println("--- [API] registraDispositivoPush fallita: ${e.message} ---")
            false
        }
    }

    /**
     * Al logout: il dispositivo smette di ricevere le notifiche dell'utente uscito. [sessione] è il
     * token di chi esce, passato a parte perché quello globale a quel punto è già stato tolto.
     */
    suspend fun rimuoviDispositivoPush(token: String, sessione: String?): Boolean {
        return try {
            client.delete("$BASE_URL/api/notifications/device") {
                sessione?.let { header(HttpHeaders.Authorization, "Bearer $it") }
                parameter("token", token)
            }.status == HttpStatusCode.OK
        } catch (e: Exception) {
            false
        }
    }

    // --- "Avvisami quando…": ricerche salvate ---
    suspend fun getRicercheSalvate(utenteId: String): List<RicercaSalvata>? = try {
        client.get("$BASE_URL/api/ricerche") { parameter("utenteId", utenteId) }.body()
    } catch (e: Exception) {
        null
    }

    /** Codice HTTP (201 creata, 409 limite di 10 raggiunto), 0 se la rete non risponde. */
    suspend fun salvaRicerca(r: RicercaSalvata): Int = try {
        client.post("$BASE_URL/api/ricerche") {
            contentType(ContentType.Application.Json)
            setBody(r)
        }.status.value
    } catch (e: Exception) {
        0
    }

    suspend fun cancellaRicerca(id: String, utenteId: String): Boolean = try {
        client.delete("$BASE_URL/api/ricerche/$id") { parameter("utenteId", utenteId) }.status == HttpStatusCode.OK
    } catch (e: Exception) {
        false
    }

    suspend fun markNotificationsAsRead(utenteId: String): Boolean {
        return try {
            val response = client.post("$BASE_URL/api/notifications/mark-read") {
                parameter("utenteId", utenteId)
            }
            response.status == HttpStatusCode.OK
        } catch (e: Exception) {
            false
        }
    }

    suspend fun deleteNotification(id: String): Boolean {
        return try {
            val response = client.delete("$BASE_URL/api/notifications/$id")
            response.status == HttpStatusCode.OK
        } catch (e: Exception) {
            false
        }
    }

    // --- NUOVE API GEOGRAFICHE ---
    suspend fun getRegioni(): List<String> {
        return try {
            client.get("$BASE_URL/api/geo/regioni").body()
        } catch (e: Exception) {
            emptyList()
        }
    }

    suspend fun getProvince(regione: String): List<String> {
        return try {
            client.get("$BASE_URL/api/geo/province") {
                parameter("regione", regione)
            }.body()
        } catch (e: Exception) {
            emptyList()
        }
    }

    suspend fun getComuni(provincia: String): List<String> {
        return try {
            client.get("$BASE_URL/api/geo/comuni") {
                parameter("provincia", provincia)
            }.body()
        } catch (e: Exception) {
            emptyList()
        }
    }

    suspend fun getCap(comune: String): List<String> {
        return try {
            client.get("$BASE_URL/api/geo/cap") {
                parameter("comune", comune)
            }.body()
        } catch (e: Exception) {
            emptyList()
        }
    }

    suspend fun searchComuni(query: String): List<String> {
        return try {
            client.get("$BASE_URL/api/geo/comuni/search") {
                parameter("query", query)
            }.body()
        } catch (e: Exception) {
            emptyList()
        }
    }

    suspend fun getSubscriptionStatus(userId: String): UserStatus {
        // Chiamiamo il BACKEND (PAYMENT_API_URL) e non la vetrina
        return client.get("$PAYMENT_API_URL/v1/status/$PAYMENT_APP_ID/$userId").body()
    }
}
